import { useMemo } from 'react';
import { resolvePrices } from '../../common/utils/priceUtils';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import { getFilteredHeaders } from '../../public/site/onlineModel/onlineModelService.js';
import { toProductGridProduct } from '../common/cmsTemplatesUtils.js';
import { ProductGridContainer } from './ProductGridContainer.js';
import type { CatalogProduct } from '../../common/utils/catalogUtils.js';
import type { OnlineModelHeader } from '../../generated/api/onlineModelHeader.js';
import type { CategoryListData } from '../cmsSchema.js';
import type { ProductGridProduct } from '@elisa-luukku-sites/cms-templates/types/src/utils/onlinefirst.js';

export type ProductGridItem = CatalogProduct & { product?: ProductGridProduct };
export const CategoryList = ({
  categoryListData,
  onlineModelHeaders,
}: {
  categoryListData: CategoryListData;
  onlineModelHeaders: OnlineModelHeader[];
}) => {
  const companyInfo = useSessionData().companyInfo;
  const { authenticatedUser, isAuthenticated } = useAuth();
  const { onAnalyticsEvent, eCommerceEvents, numberOfItems, title, titleTag, content } = categoryListData;
  const { priceAlteredModels, fetchHeadersByCodes, isHeaderCodeResolved } = useOnlineModelsContext();
  const visibleProductOnlineModelCodes = (productCodes: string[]) => {
    if (isAuthenticated && fetchHeadersByCodes) {
      fetchHeadersByCodes(productCodes);
    }
  };

  const productGridItems = useMemo(() => {
    const mapOnlineModeHeaderToCatalogProduct = (headers: OnlineModelHeader[]): CatalogProduct[] => {
      return getFilteredHeaders(headers, companyInfo, authenticatedUser, priceAlteredModels?.onlineModelHeadersByCode)
        .filter(header => header.lowestPrices.length > 0)
        .map(onlineModelHeader => {
          const {
            created,
            listingImage,
            manufacturer,
            offerBadge,
            onlineModelCode,
            onlineModelName,
            pagePath,
            tags = [],
          } = onlineModelHeader;
          return {
            created,
            code: onlineModelCode,
            imageUrl: listingImage,
            manufacturer,
            name: onlineModelName,
            offerBadge,
            pagePath,
            ref: onlineModelHeader,
            tags,
            ...resolvePrices(onlineModelHeader.lowestPrices.flatMap(lp => lp.prices)),
            product: toProductGridProduct(
              authenticatedUser,
              onlineModelHeader,
              undefined,
              priceAlteredModels?.onlineModelHeadersByCode,
              true,
              isAuthenticated ? !isHeaderCodeResolved(onlineModelHeader.onlineModelCode) : false
            ),
          };
        });
    };
    if (isAuthenticated) {
      setTimeout(() => {
        // A bit annoying, but this makes sure that the products first sorted get loaded earlier than the later ones.
        fetchHeadersByCodes(onlineModelHeaders.map(header => header.onlineModelCode));
      }, 250);
    }
    return mapOnlineModeHeaderToCatalogProduct(onlineModelHeaders);
  }, [
    onlineModelHeaders,
    authenticatedUser,
    companyInfo,
    priceAlteredModels,
    isAuthenticated,
    fetchHeadersByCodes,
    isHeaderCodeResolved,
  ]);

  if (onlineModelHeaders.length === 0) {
    return null;
  }

  // Due to how the sites-templates are now built we no longer get the contentBlockId at this level
  // Thus we have to build some other value to differentiate between multiple category lists on a page
  // The aforementioned functionality is required for the persistence of search/filter/sort params on categorylists
  const contentBlockId = categoryListData.productsWithTags?.toString()?.replace(',', '');

  return (
    <ProductGridContainer
      items={productGridItems}
      content={content}
      title={title}
      titleTag={titleTag}
      category={onlineModelHeaders[0].category}
      onAnalyticsEvent={onAnalyticsEvent}
      eCommerceEvents={eCommerceEvents}
      itemLimit={numberOfItems}
      id={contentBlockId}
      setVisibleProductCodes={visibleProductOnlineModelCodes}
    />
  );
};
